import { describirEntrega } from "@/lib/fechas";
import type { Cotizacion, Informe } from "@/lib/informes";
import { arbolDeContenidos } from "@/lib/seleccionDeContenidos";

import { DatoDelInforme as Dato } from "./DatoDelInforme";
import {
  describirRetasacion,
  describirRol,
  describirTasacion,
  formatearDecimal,
} from "./formato";

/**
 * Lo que el revisor mira antes de visar una cotización (RF-G2, pantalla de
 * visado del Módulo G).
 *
 * NO ES EL DOCUMENTO, aunque se le parezca, y la diferencia es de destinatario:
 *
 * - El documento es el papel que RECIBE EL CLIENTE: portada, carta, cuadro de
 *   honorarios y condiciones de pago. No se mira dentro de la aplicación: se
 *   DESCARGA ({@see BotonDescargarCotizacion}), porque lo que el equipo
 *   necesita es el archivo.
 * - Esta es la ficha de CONTROL del revisor: sin membrete —no se está emitiendo
 *   nada todavía— y con los datos que hay que verificar antes de dejar salir la
 *   cotización. Habla de "valor cotizado", que es lo que se está aprobando.
 *   Por eso sigue siendo HTML: no es un papel, es una lista de chequeo.
 *
 * Cliente y propiedad NO van adentro: son el encabezado de la pantalla, porque
 * identifican QUÉ se está visando y no son un dato más a verificar.
 *
 * Componente de presentación puro: recibe el informe y la cotización ya
 * resueltos, no consulta nada.
 */
export function FichaDeVisado({
  informe,
  cotizacion,
}: {
  informe: Informe;
  cotizacion: Cotizacion;
}) {
  const roles = informe.roles ?? [];
  const unidad = informe.unidad_superficie ?? "ha";
  const contenidos = informe.contenidos ?? [];
  const items = informe.items ?? [];
  const superficie = formatearDecimal(informe.superficie_ha);
  const precio = formatearDecimal(cotizacion.precio_uf);

  return (
    <article className="bg-card space-y-6 rounded-xl border p-6 sm:p-8">
      {/* Los roles abren la ficha y llevan el conteo en el rótulo: RF-E1 §8.2
          hace que la suma de sus superficies se compare contra la declarada,
          así que es lo primero que el revisor contrasta. */}
      <Dato etiqueta={`Roles y comunas (${roles.length})`}>
        {roles.length > 0 ? (
          <ul className="space-y-1">
            {roles.map((rol) => (
              <li key={rol.id} className="text-ink-body text-body">
                {describirRol(rol.rol_sii, rol.comuna, rol.superficie, unidad)}
              </li>
            ))}
          </ul>
        ) : null}
      </Dato>

      <div className="grid gap-6 sm:grid-cols-2">
        <Dato etiqueta="Dirección de la propiedad" valor={informe.direccion} />
        <Dato etiqueta="Superficie" valor={superficie ? `${superficie} há` : null} />
      </div>

      <Dato etiqueta="Descripción de la ubicación" valor={informe.descripcion_ubicacion} />

      <div className="grid gap-6 sm:grid-cols-2">
        <Dato etiqueta="Tasación a nombre de" valor={informe.solicitante_nombre} />
        <Dato etiqueta="Contacto" valor={informe.telefono_contacto} />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Dato etiqueta="Tipo de tasación / formato de salida" valor={describirTasacion(informe)} />
        {/* `detallado`: acá se nombra la base histórica porque es justo lo que
            el revisor verifica (RF-E3). En el documento del cliente esa
            precisión sobra. */}
        <Dato
          etiqueta="¿Es retasación?"
          valor={describirRetasacion(informe.es_retasacion, { detallado: true })}
        />
      </div>

      {items.length > 0 ? (
        /* Los ítems de la plantilla, tal como quedaron CONGELADOS en el
           informe. Los informes anteriores a las plantillas no tienen ítems y
           caen en la lista plana de abajo. */
        <Dato etiqueta={`Contenidos incluidos (${items.length})`}>
          {informe.plantilla ? (
            <p className="text-ink-muted text-sm">Plantilla: {informe.plantilla.nombre}</p>
          ) : null}
          <ul className="mt-2 space-y-2">
            {arbolDeContenidos(items).map((grupo) => (
              <li key={grupo.grupo}>
                {grupo.subgrupos.map((sub) => (
                  <div key={sub.subgrupo ?? "—"} className="mb-1">
                    <p className="text-ink text-sm font-bold">
                      {sub.subgrupo ? `${grupo.grupo} › ${sub.subgrupo}` : grupo.grupo}
                    </p>
                    <ul className="text-ink-body ml-4 list-disc text-sm">
                      {sub.items.map((item) => (
                        <li key={item.maestro_item_id}>
                          {item.nombre}
                          {item.obligatoriedad === "condicional" && item.condicion
                            ? ` · ${item.condicion}`
                            : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        </Dato>
      ) : (
        <Dato
          etiqueta="Contenidos incluidos"
          valor={contenidos.length > 0 ? contenidos.map((c) => c.nombre).join(", ") : null}
        />
      )}

      {/* Las dos cifras que se aprueban, destacadas y juntas: el visado es
          exactamente decidir sobre ellas. */}
      <div className="bg-muted flex flex-wrap items-end justify-between gap-6 rounded-xl px-6 py-5">
        <div>
          <p className="text-label text-ink-muted uppercase">Valor cotizado</p>
          <p className="text-h2 text-ink mt-1 font-mono">{precio ?? "—"} UF</p>
        </div>
        <div className="text-right">
          <p className="text-label text-ink-muted uppercase">Entrega</p>
          <p className="text-h2 text-ink mt-1 font-mono">
            {describirEntrega(cotizacion.fecha_entrega, cotizacion.plazo_dias_habiles)}
          </p>
        </div>
      </div>
    </article>
  );
}
